import { Flex, Stack, Loader } from '@grubhubprod/gh-cookbook-react';
import React, { useEffect, useState } from 'react';
import { AllowanceAmountForm } from 'src/components/oneCorp/features/MealCredit/components/People/AllowanceAmountForm/AllowanceAmountForm';
import { AllowanceAmountInfo } from 'src/components/oneCorp/features/MealCredit/components/People/AllowanceAmountForm/AllowanceAmountInfo';
import {
  TagInitialization,
  useAssignAllowanceAmount,
} from 'src/components/oneCorp/features/MealCredit/hooks/useAssignAllowanceAmount';
import { useManageMealCreditDiners } from 'src/components/oneCorp/features/MealCredit/hooks/useManageMealCreditDiners';
import { useMealCreditDinerSearch } from 'src/components/oneCorp/features/MealCredit/hooks/useMealCreditDinerSearch';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';
import { getMealCreditAllowanceAnalyticsEvent } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditAnalytics.utils';
import { PeopleSearchForm } from 'src/components/oneCorp/features/MealCredit/components/People/PeopleSearchForm/PeopleSearchForm';
import { PeopleTable } from 'src/components/oneCorp/features/MealCredit/components/People/PeopleTable/PeopleTable';
import { LocTagInvite } from 'src/types/LineOfCredit';

export interface PeopleSectionControllerProps {
  corpId: string;
  mealCreditId: string;
  tagInvites: LocTagInvite[];
  onAssignAllowanceAmount: () => Promise<void>;
  onTotalResultsChange?: (count: number) => void;
}

export enum PeopleSectionVisibility {
  LOADER,
  FORM,
  TABLE,
}

const TagInitializationToPeopleSectionVisibilityMap = new Map([
  [TagInitialization.INITIALIZED, PeopleSectionVisibility.TABLE],
  [TagInitialization.NOT_INITIALIZED, PeopleSectionVisibility.FORM],
  [TagInitialization.PROCESSING, PeopleSectionVisibility.LOADER],
]);

export default function PeopleSectionController({
  corpId,
  mealCreditId,
  tagInvites,
  onAssignAllowanceAmount,
  onTotalResultsChange,
}: PeopleSectionControllerProps): JSX.Element {
  const [peopleSectionVisibility, setPeopleSectionVisibility] =
    useState<PeopleSectionVisibility>(PeopleSectionVisibility.LOADER);

  const { sendAnalytics } = useSendAnalytics({
    eventcategory: 'meal credit settings',
  });

  const {
    searchDinersByQuery,
    totalResults,
    resultsList,
    totalPages,
    currentSearchPage,
    pageChange,
    isLoading,
    hasNoResultsWithQuery,
    hasNoResultsWithoutQuery,
    isUsingQuery,
  } = useMealCreditDinerSearch({
    corpId,
    mealCreditId,
  });

  const { isRemoving, isAdding, removePeople, addPeople } =
    useManageMealCreditDiners({
      corpId,
      mealCreditId,
      onSuccess: async () => {
        await searchDinersByQuery('');
      },
    });

  const { updateAllowanceAmount, amount, tagInitializationStatus } =
    useAssignAllowanceAmount({
      corpId,
      mealCreditId,
      tagInvites,
      onSuccess: handleAssignAllowanceSuccess,
    });

  async function handleAssignAllowanceSuccess(): Promise<void> {
    const eventaction = getMealCreditAllowanceAnalyticsEvent(
      tagInitializationStatus
    );
    if (eventaction) {
      sendAnalytics({ eventaction });
    }
    await onAssignAllowanceAmount();
    await searchDinersByQuery('');
  }

  useEffect(() => {
    if (!isUsingQuery) {
      onTotalResultsChange?.(totalResults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalResults, isUsingQuery]);

  useEffect(() => {
    const peopleSectionVisibilityToSet =
      TagInitializationToPeopleSectionVisibilityMap.get(
        tagInitializationStatus
      );
    setPeopleSectionVisibility(peopleSectionVisibilityToSet);
  }, [tagInitializationStatus, tagInvites]);

  useEffect(() => {
    if (tagInitializationStatus === TagInitialization.INITIALIZED) {
      searchDinersByQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId, mealCreditId, tagInitializationStatus]);

  switch (peopleSectionVisibility) {
    case PeopleSectionVisibility.FORM:
      return (
        <AllowanceAmountForm
          onFormSubmit={async ({ allowanceAmount }): Promise<void> =>
            updateAllowanceAmount(allowanceAmount)
          }
          onCancel={async (): Promise<void> => {
            setPeopleSectionVisibility(PeopleSectionVisibility.TABLE);
            await searchDinersByQuery('');
          }}
          amount={amount}
          isCancelButtonDisabled={
            tagInitializationStatus === TagInitialization.NOT_INITIALIZED
          }
        />
      );
    case PeopleSectionVisibility.TABLE:
      return (
        <>
          <Stack y="md">
            <AllowanceAmountInfo
              amount={amount}
              onEdit={(): void =>
                setPeopleSectionVisibility(PeopleSectionVisibility.FORM)
              }
            />
          </Stack>
          <Stack y="md">
            <PeopleSearchForm onSubmit={searchDinersByQuery} />
          </Stack>
          <PeopleTable
            totalResults={totalResults}
            resultsList={resultsList}
            totalPages={totalPages}
            currentSearchPage={currentSearchPage}
            pageChange={pageChange}
            isLoading={isLoading}
            hasNoResultsWithQuery={hasNoResultsWithQuery}
            hasNoResultsWithoutQuery={hasNoResultsWithoutQuery}
            addPeople={addPeople}
            isAdding={isAdding}
            isRemoving={isRemoving}
            removePeople={removePeople}
          />
        </>
      );
    case PeopleSectionVisibility.LOADER:
    default:
      return (
        <Flex alignItems="center" justifyContent="center">
          <Loader size="md" />
        </Flex>
      );
  }
}
